import { BaseMapManager } from '@/core/mapmanager'
import { PointManager } from '@/points/PointManager.ts'
import { LocationManager, LocationCache } from '@/services/Location.js'
import Recaptcha from '@/utils/recaptcha.ts'
import PointService from '@/services/PointService.ts'
import AreaLabels from '@/services/AreaLabels.ts'

class MapManager extends BaseMapManager {
  constructor() {
    super()
  }
}

/**
 * マップ単位の操作を担当
 * 
 * init -> onIdle -> update
 */
export default {
  /**
   * マップ設定の制約値
   */
  CONSTRAINTS: {
    /**
     * 緯度の有効範囲
     */
    LATITUDE: {
      MIN: -90,
      MAX: 90
    },
    /**
     * 経度の有効範囲
     */
    LONGITUDE: {
      MIN: -180,
      MAX: 180
    },
    /**
     * ズームレベルの有効範囲
     */
    ZOOM_LEVEL: {
      MIN: 0,
      MAX: 22
    }
  },
  /**
   * マップ
   * @class {google.maps.Map}
   */
  map: null,
  /**
   * 最小ズームレベル
   * これを下回るとPointを非表示にする
   */
  minZoom: storelocator.map?.location?.minZoom != undefined
    ? storelocator.map.location.minZoom
    : 11,
  /**
   * ズームレベルが最小を下回っている？
   * TODO 必要？minZoonで十分では？
   */
  isZoomLimited: false,
  /**
   * ズームレベルの下限を下回り、Pointを表示しないことを示すのに使う
   * TODO 必要？minZoonで十分では？
   */
  isShowZoomLimitMessage: true,
  /**
   * 表示・ズームのキャッシュ
   */
  locationCache: new LocationCache().restore(),
  /**
   * @class {MapManager}
   */
  manager: new MapManager(),
  /**
   * ページごとの事情を加味した方がいいので、初期化は任せる
   * @class {PointManager}
   */
  pointManager: null,
  /**
   * 現在地まわり
   * @class {LocationManager}
   */
  locationManager: null,
  /**
   * 地名検索で確定した地点を示すマーカー（1本を更新し続ける）
   */
  searchPointMarker: null as google.maps.marker.AdvancedMarkerElement | null,
  /**
   * 地名検索で得た座標に検索地点マーカーを表示（または位置更新）
   */
  updateSearchMarker(
    position: google.maps.LatLng | null | undefined
  ) {
    if (!this.map) {
      return
    }
    const latLng = position
    if (!latLng) {
      return
    }

    if (this.searchPointMarker) {
      this.searchPointMarker.position = latLng
      return
    }

    const wrap = document.createElement('div')
    wrap.className = 'marker on-map map-search-result-marker'
    wrap.style.cssText = 'pointer-events:none; line-height:0;'
    wrap.innerHTML = '<img src="/assets/images/icon/searchPoint.svg">'

    this.searchPointMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: latLng,
      content: wrap,
    })
  },
  /**
   * 検索地点マーカーを除去（現在地へ移動成功時など）
   */
  clearSearchMarker() {
    if (this.searchPointMarker) {
      this.searchPointMarker.map = null
      this.searchPointMarker = null
    }
  },
  /**
   * 初期化
   * @param {Object} options PointManagerにそのまま渡す（乱暴かも、まずは更新点を絞りたい）
   * {
   *   location: {
   *     zoom: ズームレベル
   *   },
   *   locationManager: 現在地マネージャの引数 options
   *   pointManager: PointManagerの引数 options
   * }
   * @param {RecaptchaActions} action reCAPTCHAアクション名
   */
  async init(options, action:string) {
    // console.debug('init')

    // reCAPTCHAが有効な場合は評価を実施する
    if (Recaptcha.isEnabled && action) {

      let isHideMap = false
      const latestResult = Recaptcha.getRecaptchaResult()

      if (latestResult !== 'beforeVerify') {
        // 評価結果がある場合はそれを利用、nullの場合は意図せず評価ができなかったため許容する
        isHideMap = latestResult === false

      } else if (Recaptcha.isApplyRecaptcha(action)) {
        // 評価結果がなく、条件を満たしていたらリキャプチャを実行        
        const token = await Recaptcha.publishToken(action)

        if (token === 'cancelled') {
          // ユーザーキャンセルは地図非表示
          isHideMap = true

        } else if (token) {
          // サーバーサイドで評価を取得
          const isSuccess = await Recaptcha.verify(token, action)
          isHideMap = isSuccess === false
        }
      }

      if (isHideMap) {
        return false
      }
    }

    // デフォルトのマップオプション
    const mapOptions: google.maps.MapOptions = {
      zoom: options.location?.zoom || this.locationCache.default.zoom || 16,
      center: new google.maps.LatLng(
        this.locationCache.default.lat,
        this.locationCache.default.lng
      ),
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapId: storelocator.google_maps.map_id,
      clickableIcons: false,
      mapTypeControl: false,
      cameraControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
      fullscreenControl: false,
      scaleControl: true,
      gestureHandling: "greedy",
      streetViewControl: true,
      streetViewControlOptions: {
        position: storelocator.page === 'map'
          ? google.maps.ControlPosition.RIGHT_CENTER
          : google.maps.ControlPosition.LEFT_BOTTOM,
      },
      keyboardShortcuts: false
    }

    // google_maps.optionsをマージ
    const googleMapsOptions: any = storelocator.page === 'map'
      ? (storelocator.map as any)?.google_maps?.options
      : (storelocator.point as any)?.google_maps?.options

    if (googleMapsOptions) {
      // その他のオプションもマージ（colorScheme以外）
      Object.keys(googleMapsOptions).forEach((key: string) => {
        (mapOptions as any)[key] = googleMapsOptions[key]
      })
    }

    this.map = new google.maps.Map(
      document.getElementById('map_canvas'),
      mapOptions
    )

    storelocator.analytics.client?.send(
      storelocator.page === 'point' ? 'ShowMapInPoint' : 'ShowMap',
      {}
    )

    this.pointManager = new PointManager(
      this.map,
      options?.pointManager || {}
    )

    this.enableDoubleTouchMoveZoom()

    this.geocoder = new google.maps.Geocoder()
    this.places = new google.maps.places.PlacesService(this.map)

    // メッセージ表示用
    this.msg_el = document.createElement('div')
    this.msg_el.classList.add('map-message')
    this.msg_el.innerHTML = ''
    this.map.getDiv().appendChild(this.msg_el)

    // 現在地
    this.locationManager = new LocationManager(
      this.map,
      options?.locationManager || {}
    )

    const lm = this.locationManager
    const prevOnSucceeded = lm.onSucceededGetLocation.bind(lm)
    lm.onSucceededGetLocation = (location: google.maps.LatLng) => {
      this.clearSearchMarker()
      prevOnSucceeded(location)
    }

    // イベント
    google.maps.event.addListener(this.map, 'idle', () => {
      this.onIdle()
    })

    google.maps.event.addListener(this.map, 'click', () => {
      this.onClicked()
    })

    google.maps.event.addListener(this.map.getStreetView(), 'visible_changed', () => {
      this.onStreetViewToggled()
    })

    return true
  },
  /**
   * クリック時
   */
  onClicked() {
  },
  /**
   * onIdleの後に発火
   */
  afterIdle() {
  },
  /**
   * 地図の操作ごとに発火
   */
  onIdle() {
    this.locationCache.save(this.map.getCenter(), this.map.getZoom())
    this.locationManager.update()
    this.afterIdle()
  },
  /**
   * ストリートビュー表示切り替え前に発火
   * ルート検索のように競合する部品を隠すような仕込みのために用意
   */
  onBeforeStreetViewToggled() {
  },
  /**
   * ストリートビュー表示切り替え時
   * 
   * イベントを送信
   * 店舗ページでは店舗がわかるように送信
   * 現在地は送信しない
   */
  onStreetViewToggled() {
    this.onBeforeStreetViewToggled()

    // イベント送信
    const streetView = this.map.getStreetView()

    if (streetView && streetView.getVisible()) {
      const eventParams: {[key: string]: string | number | boolean} = {}

      // 店舗ページの場合は店舗情報を追加
      if (storelocator.page === 'point' && storelocator.point?.point) {
        const point = storelocator.point.point
        eventParams.PointKey = point.key
        eventParams.PointName = point.name
        eventParams.PointAddress = point.address
      }

      storelocator.analytics.client?.send('ShowStreetView', eventParams)
    }
  },
  /**
   * 全体の更新
   * 
   * Pointのマーカー
   * ズームによる非表示制御＋メッセージ
   * @param useLatestBounds true: Pointの読み込み範囲を前回取得したBoundsにする（ループ防止用）
   * @returns PointManager.render()の結果
   */
  update(useLatestBounds = false): { isClusterUpdated: boolean, pointsInBounds: Point[] } {
    const zoom = this.map.getZoom()

    AreaLabels.onMapUpdated(zoom)

    if (this.minZoom > zoom) {
      // 最寄り店の案内を削除
      PointService.clearGuide()
      // マーカーが消えると詳細の指し先が無くなるので、Pointの選択も解除する
      // マーカー側の選択解除がクラスタを辿るため、hideAll()より前に呼ぶ
      PointService.deselectPoint()
      // マーカー非表示
      this.pointManager.hideAll()

      this._showZoomOutedMessage()

      this.isZoomLimited = true

      return {
        isClusterUpdated: true,
        pointsInBounds: []
      }
    }

    if (this.isZoomLimited) {
      this.isZoomLimited = false
      this.hideMessage()
    }

    return this.pointManager.render(useLatestBounds)
  },
  /**
   * ズームを促すメッセージを出す
   *
   * ズームレベルの下限を下回っていることは呼び出し側で判断済みなので、ここでは見ない
   */
  _showZoomOutedMessage() {
    // エリアグループラベルを出せるなら、ズームを引いたままでも店舗の在り処は読める
    if (AreaLabels.canShow) {
      return
    }

    this.showMessage(
      storelocator.page === 'map'
        ? storelocator.messages.map_zoom_outed
        : storelocator.messages.point_zoom_outed
      , 0)
  },
  /**
   * ダブルタップでのズーム用 TODO 後で切り離し
   */
  _click_ts: null,
  /**
   * ダブルタップでのズーム用 TODO 後で切り離し
   */
  _drag_zoom: false,
  /**
   * ダブルタップでのズーム用 TODO 後で切り離し
   */
  _drag_zoom_y: null,
  /**
   * ダブルタップからの上下ドラッグによるズームを有効にする
   * GMPが生成するDOMにイベントを送信するため、GMPの実装が変わると動かなくなる可能性あり
   * 
   * TODO 後で切り離し
   */
  enableDoubleTouchMoveZoom() {
    // マーカークリック等には反応しないよう、最初の他イップはマップのイベントから取得
    this.map.addListener('click', (_e) => {
      this._click_ts = Date.now()
    })

    const el = this.map.getDiv()
    el.addEventListener('touchstart', (_e) => { 
      // ダブルクリックの閾値の標準は500msのようだが、500ms だと少し長いので、やや短めの300msとする
      if (Date.now() - this._click_ts < 300) {
        this._drag_zoom = true
        this._drag_zoom_y = e.touches[0].screenY
      }
    })

    el.addEventListener('touchmove', (e) => {
      if (this._drag_zoom) {
        // ドラッグイベントをスクロールイベントに置き換える
        // （setZoom で実装すると、もっさりしてイマイチだった）

        const diff = e.touches[0].screenY - this._drag_zoom_y
        this._drag_zoom_y = e.touches[0].screenY

        // GMPの実装に依存するが、やむを得ない
        const target = el.querySelector("div.gm-style > div:nth-child(1)")
        const rect = el.getBoundingClientRect()
        // タップした位置に関わらず、画面中央を中心としたズームとする（公式の仕様がそうなっている）
        const event = new WheelEvent("wheel", {
          clientX: rect.x + rect.width / 2,
          clientY: rect.y + rect.height / 2,
          deltaY: diff * (-1), // ドラッグ量とスクロール量が同じ値で丁度良い
          deltaMode: 0x00
        })
        target.dispatchEvent(event);
      }
    })

    el.addEventListener('touchend', () => {
      this._drag_zoom = false
    })
  },
  fitBounds(bounds: google.maps.LatLngBounds, max_zoom: number) {
    if (!max_zoom) {
      this.map.fitBounds(bounds)
      return
    }

    let zoom = this.getZoomByBounds(bounds)
    if (zoom > max_zoom) {
        zoom = max_zoom
    }

    this.map.panTo(bounds.getCenter())
    this.map.setZoom(zoom)

    return zoom
  },
  getZoomByBounds(bounds: google.maps.LatLngBounds) {
    const mapType = this.map.mapTypes.get(this.map.getMapTypeId())
    if (!mapType) {
      return
    }

    const maxZoom = mapType.maxZoom ? mapType.maxZoom : 21
    const minZoom = mapType.minZoom ? mapType.minZoom : 0
    const northEast = this.map.getProjection().fromLatLngToPoint(bounds.getNorthEast())
    const southWest = this.map.getProjection().fromLatLngToPoint(bounds.getSouthWest())
    const worldCoordWidth = Math.abs(northEast.x - southWest.x)
    const worldCoordHeight = Math.abs(northEast.y - southWest.y)
    const fitPad = 30

    for (let zoom = maxZoom; zoom >= minZoom; --zoom) {
      if (worldCoordWidth * (1 << zoom) + 2 * fitPad < this.map.getDiv().clientWidth && worldCoordHeight * (1 << zoom) + 2 * fitPad < this.map.getDiv().clientHeight) {
        return zoom
      }
    }

    return 0
  },
  /**
   * メッセージを表示
   * @param message 表示する文言
   * @param timeout ミリ秒で指定し、指定時間が経過すると消す、0なら消さない
   */
  showMessage(message: string, timeout = 5000) {
    console.debug('showMessage')

    this.msg_el.innerHTML = message
    this.msg_el.style.display = 'block'

    if (timeout) {
      setTimeout(() => {
        this.hideMessage()
      }, timeout)
    }
  },
  /**
   * メッセージを消す
   */
  hideMessage() {
    console.debug('hideMessage')

    this.msg_el.innerHTML = ''
    this.msg_el.style.display = 'none'
  },
  fromLatLngToPoint(lat: number, lng: number) {
    const projection = this.map.getProjection()

    const bounds = this.map.getBounds()
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()

    const p1 = projection.fromLatLngToPoint(new google.maps.LatLng(ne.lat(), sw.lng()))
    const p2 = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng))

    const scale = Math.pow(2, this.map.getZoom())

    return {
      x: (p2.x - p1.x) * scale,
      y: (p2.y - p1.y) * scale
    }
  },
  adjastMap() {
    if (!this.pointManager) {
      return
    }

    if (this.pointManager.selectedPoint.bounds == null) {
      return
    }
    const pointBounds = this.pointManager.selectedPoint.bounds
    const mapBounds = this.map.getBounds()
    if (!mapBounds.intersects(pointBounds)) {
      console.debug("マップ位置調整")
      this.map.panTo(pointBounds.getCenter())
    }
  },
  /**
   * boundsの拡大
   * 引数のboundsを東西南北方向に X 倍する
   * @param {*} bounds ?
   * @param {*} xn ?
   * @param {*} xe ?
   * @param {*} xs ?
   * @param {*} xw ?
   * @returns ?
   */
  extendBounds(bounds: google.maps.LatLngBounds, xn: number, xe: number, xs: number, xw: number) {
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const extend_x = (ne.lng() - sw.lng())
    const extend_y = (ne.lat() - sw.lat())

    const n = ne.lat() + extend_y * xn
    const e = ne.lng() + extend_x * xe
    const s = sw.lat() - extend_y * xs
    const w = sw.lng() - extend_x * xw

    const newBounds = new google.maps.LatLngBounds()
    newBounds.extend({lat: n, lng: e})
    newBounds.extend({lat: s, lng: w})

    return newBounds
  },
  /**
   * クエリパラメータからマップ位置情報を取得し、マップを移動する
   *
   * クエリパラメータ:
   * - latlng: "緯度,経度" の形式 (例: "34.668892,135.501069")
   * - z: ズームレベル (例: "14")
   *
   * @returns {boolean} true: クエリパラメータが有効で移動 false: それ以外
   */
  panToQueryParams(): boolean {
    const urlParams = new URLSearchParams(window.location.search)
    const latlngParam = urlParams.get('latlng')
    const zoomParam = urlParams.get('z')

    if (!latlngParam) {
      return false
    }

    // latlng パラメータのパース
    const latlngParts = latlngParam.split(',')
    if (latlngParts.length !== 2) {
      console.warn('Invalid latlng parameter format. Expected: "lat,lng"')
      return false
    }

    const lat = parseFloat(latlngParts[0])
    const lng = parseFloat(latlngParts[1])

    // 数値チェック
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      console.warn('Invalid latlng parameter: lat or lng is not a number')
      return false
    }

    // 有効な緯度経度の範囲でバリデーション
    const { LATITUDE, LONGITUDE, ZOOM_LEVEL } = this.CONSTRAINTS

    if (lat < LATITUDE.MIN || lat > LATITUDE.MAX || lng < LONGITUDE.MIN || lng > LONGITUDE.MAX) {
      console.warn(
        `latlng is out of valid range (lat: ${LATITUDE.MIN} to ${LATITUDE.MAX}, lng: ${LONGITUDE.MIN} to ${LONGITUDE.MAX})`
      )
      return false
    }

    // ズームレベルのバリデーション
    let zoom: number | null = null

    if (zoomParam) {
      zoom = parseInt(zoomParam, 10)

      if (Number.isNaN(zoom) || zoom < ZOOM_LEVEL.MIN || zoom > ZOOM_LEVEL.MAX) {
        console.warn(`Invalid zoom level. Expected: ${ZOOM_LEVEL.MIN}-${ZOOM_LEVEL.MAX}`)
        zoom = null
      }
    }

    // マップを移動
    const position = new google.maps.LatLng(lat, lng)
    this.map.panTo(position)

    if (zoom !== null) {
      this.map.setZoom(zoom)
    }

    // console.debug(`Map moved to query params: lat=${lat}, lng=${lng}, zoom=${zoom}`)

    return true
  }
}
